/**
 * Ridalo traffic statistics — first-party, cookieless, tiny. One anonymous beacon per page view / click, sent AFTER the page
 * is idle, never blocking rendering. Nothing is stored in the browser (no cookie, no localStorage). The server keeps counters
 * only (see backend/services/site_metrics.py). Skipped for administrators, bots/automation and visitors who send Do-Not-Track
 * or Global Privacy Control.
 */
const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`;

export const trackingAllowed = ({ isAdmin = false } = {}) => {
  if (isAdmin || typeof navigator === 'undefined') return false;
  if (navigator.webdriver) return false;
  if (navigator.doNotTrack === '1' || window.doNotTrack === '1' || navigator.globalPrivacyControl === true) return false;
  return true;
};

/** Service category of a link, from its path only: /scooters… -> scooter, /transport… -> transfer, /services/… -> excursion. */
export const serviceCategoryOf = (href) => {
  let path = '';
  try { path = new URL(href, 'https://ridalo.co').pathname; } catch { return null; }
  const parts = path.split('/').filter(Boolean);
  if (parts[0] === 'en' || parts[0] === 'fr') parts.shift();
  const first = (parts[0] || '').toLowerCase();
  if (first === 'scooters' || first === 'scooter') return 'scooter';
  if (first === 'transport' || first === 'airport-transfer' || first === 'transfert-aeroport-phuket') return 'transfer';
  if (first === 'services' || first === 'service') return 'services';
  return null;
};

const utmOnce = (() => {
  let sent = false;
  return () => {
    if (sent) return '';
    sent = true;
    try { return (new URLSearchParams(window.location.search).get('utm_source') || '').slice(0, 40); } catch { return ''; }
  };
})();

export const sendEvent = (event, extra = {}, { force = false } = {}) => {
  if (!force && !trackingAllowed()) return;
  const body = JSON.stringify({ e: event, ...extra });
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) return;
    fetch(ENDPOINT, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' }, credentials: 'omit' }).catch(() => {});
  } catch { /* statistics must never break the page */ }
};

const idle = (fn) => {
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn, { timeout: 3000 });
  else setTimeout(fn, 1500);
};

let lastPath = null;
export const trackPageview = (path, isAdmin = false) => {
  if (!trackingAllowed({ isAdmin }) || path === lastPath) return;
  lastPath = path;
  idle(() => sendEvent('pageview', { p: path, r: document.referrer || '', s: utmOnce() }));
};

/** One passive listener for the whole page: a click on a link to a Ridalo service counts once, by category. */
export const onDocumentClick = (event, isAdmin = false) => {
  if (!trackingAllowed({ isAdmin })) return;
  const a = event.target && event.target.closest ? event.target.closest('a[href]') : null;
  if (!a) return;
  const category = serviceCategoryOf(a.getAttribute('href'));
  if (category) sendEvent('service_click', { c: category });
};

export const resetForTests = () => { lastPath = null; };
